import styled from "@emotion/styled";
import { flexRender, Row } from "@tanstack/react-table";
import { ActivityDetailedView } from "components/Activity/ActivityDetailedView/ActivityDetailedView";
import { Spacer } from "components/general/Spacer/Spacer";
import type { Property } from "csstype";
import { useIsMobile } from "hooks/useDeviceType";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import { CSSProperties, FC, useMemo, useState } from "react";
import { ActivityStore } from "store/activity/ActivityStore";
import { TransactionDetail } from "types/history";
import { withIsVisible } from "utils/reactUtils";
import { ACITIVTY_MONTH_TITLE_HEIGHT } from "./ActivityTableList";
import { MonthRowContent } from "./MonthRowContent";
import { TableRowMobile } from "./TableRowMobile";

interface TableRowInterface {
  row: Row<TransactionDetail>;
  firstTxOfMonth: boolean;
  lastTxOfMonth: boolean;
  rowIndex: number;
  style: CSSProperties | undefined;
}

interface TableRowDesktopProps {
  row: Row<TransactionDetail>;
  height: Property.Height;
  onClick: (row: Row<TransactionDetail>) => void;
}

const TableRowDesktop: FC<TableRowDesktopProps> = ({ row, height, onClick }) => {
  return (
    <>
      {row.getVisibleCells().map((cell, index) => {
        const cellSize = cell.column.getSize();
        if (cellSize === 0) return null;
        return (
          <Container
            onClick={() => onClick(row)}
            style={{ width: `${cellSize}%`, height }}
            key={index}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Container>
        );
      })}
    </>
  );
};

interface DetailsRowProps {
  row: Row<TransactionDetail>;
  showBorderTop: boolean;
  lastTxOfMonth: boolean;
  showHoverState: boolean;
  height: Property.Height;
}

const DetailsRow: FC<DetailsRowProps> = ({
  row,
  showBorderTop,
  lastTxOfMonth,
  showHoverState,
  height,
}) => {
  const isMobile = useIsMobile();
  const history = ActivityStore.useState(s => s.history);
  const txHash = useMemo(() => row.getValue<TransactionDetail["txHash"]>("txHash"), [row]);
  const [isDetailedViewOpen, setIsDetailedViewOpen] = useState(false);
  const openDetailedView = () => setIsDetailedViewOpen(true);
  const closeDetailedView = () => setIsDetailedViewOpen(false);

  return (
    <>
      {isMobile ? (
        <WrapperMobile isVisible={isMobile} onClick={openDetailedView}>
          {/* Since mobile does not render a table-like structure, it does not use
        .getVisibleCells to loop through pre-formatted cells in useActivityColumnDefinitions.tsx */}
          <TableRowMobile rowDetails={row} />
        </WrapperMobile>
      ) : (
        <Wrapper
          isVisible={!isMobile}
          showBorderTop={showBorderTop}
          lastTxOfMonth={lastTxOfMonth}
          showHoverState={showHoverState}
        >
          <TableRowDesktop row={row} height={height} onClick={openDetailedView} />
        </Wrapper>
      )}
      {history.data?.results[txHash] != null && (
        <ActivityDetailedView
          open={isDetailedViewOpen}
          closeView={closeDetailedView}
          onClose={closeDetailedView}
          placement="right"
          width={`max(${isMobile ? 100 : 33}%, 400px)`}
          txHash={txHash}
        />
      )}
    </>
  );
};

interface MonthRowProps {
  isMobile: boolean;
  row: Row<TransactionDetail>;
  rowIndex: number;
}

const MonthRow = withIsVisible(({ isMobile, row, rowIndex }: MonthRowProps) => {
  return (
    <div>
      <Spacer height={isMobile ? "10px" : "12px"} />
      <MonthRowContent date={row.original.timestamp} />
    </div>
  );
});

export const TableRow = ({
  row,
  firstTxOfMonth,
  lastTxOfMonth,
  rowIndex,
  style,
}: TableRowInterface) => {
  const isMobile = useIsMobile();
  const isTradeHistoryReturned = useIsTradeHistoryReturned();

  const rowHeight = useMemo(() => {
    if (typeof style?.height === "number") {
      return (
        String(firstTxOfMonth ? style.height - ACITIVTY_MONTH_TITLE_HEIGHT : style.height) + "px"
      );
    }
    if (style?.height == null) {
      return "fit-content";
    }
    if (typeof style.height != "string" || !style.height.endsWith("px")) {
      return "fit-content";
    }
    try {
      return firstTxOfMonth
        ? String(Number(style.height.slice(0, -2)) - ACITIVTY_MONTH_TITLE_HEIGHT) + "px"
        : style?.height;
    } catch {
      return "fit-content";
    }
  }, [firstTxOfMonth, style?.height]);

  if (row == null) {
    return null;
  }

  return (
    <TableRowContainer showTopBorder={!firstTxOfMonth} isMobile={isMobile} style={style}>
      <MonthRow isVisible={firstTxOfMonth} row={row} rowIndex={rowIndex} isMobile={isMobile} />
      <DetailsRow
        row={row}
        showBorderTop={!firstTxOfMonth}
        lastTxOfMonth={lastTxOfMonth}
        showHoverState={isTradeHistoryReturned}
        height={rowHeight}
      />
    </TableRowContainer>
  );
};

// Sub-components

const TableRowContainer = styled.div<{ showTopBorder: boolean; isMobile: boolean }>`
  position: relative;
  border-top: 1px solid
    ${props => (props.showTopBorder ? props.theme.surface.main.secondary.default : "transparent")};

  &:hover {
    border-top: 1px solid transparent;
  }

  &:hover + div {
    border-top: 1px solid transparent;
  }
`;

const Wrapper = withIsVisible(styled.div<{
  showBorderTop: boolean;
  lastTxOfMonth: boolean;
  showHoverState: boolean;
}>`
  display: flex;
  max-height: 100%;
  min-height: 56px;
  width: 100%;
  border-radius: 17px;
  cursor: ${props => (props.showHoverState ? "pointer" : "inherit")};

  &:hover {
    background: ${props => props.theme.surface.main.secondary.default};
    transition: background 250ms ease-in-out;
  }
`);

const WrapperMobile = withIsVisible(styled.div`
  max-height: 100%;
  width: 100%;
  overflow: hidden;
  margin: 0;
`);

const Container = styled.div`
  display: flex;
  padding: 12px;
  margin: auto;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: 12px;
  align-self: stretch;
`;
